class WhatsAppCartInquiry extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.errorEl = this.querySelector('[data-whatsapp-cart-error]');
    this.submitLabel = this.querySelector('[data-whatsapp-cart-submit-label]');
    this.confirmStep = this.querySelector('[data-whatsapp-step="confirm"]');
    this.confirmButton = this.querySelector('[data-whatsapp-confirm]');
    this.confirmLabel = this.querySelector('[data-whatsapp-confirm-label]');
    this.confirmError = this.querySelector('[data-whatsapp-confirm-error]');
    this.openLink = this.querySelector('[data-whatsapp-open]');
    this.phone = (this.getAttribute('data-phone') || '').replace(/\D/g, '');
    this.webhookUrl = this.getAttribute('data-webhook-url') || '';
    this.shopName = this.getAttribute('data-shop-name') || '';
    this.hidePrices = this.getAttribute('data-hide-prices') === 'true';
    this.defaultLabel = this.submitLabel ? this.submitLabel.textContent : 'Send Order via WhatsApp';
    this.cart = null;
    this.order = null;

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.confirmButton?.addEventListener('click', this.onConfirm.bind(this));
    this.querySelector('[data-whatsapp-back]')?.addEventListener('click', () => this.showStep('details'));
  }

  connectedCallback() {
    this.refreshCart();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      this.cart = await response.json();
    } catch (error) {
      console.warn('Could not load cart for WhatsApp order', error);
    }
    return this.cart;
  }

  showError(message, el = this.errorEl) {
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  clearErrors() {
    [this.errorEl, this.confirmError].forEach((el) => {
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
    });
  }

  showStep(step) {
    this.clearErrors();
    const confirm = step === 'confirm';
    this.form.hidden = confirm;
    if (this.confirmStep) this.confirmStep.hidden = !confirm;
    this.setAttribute('data-active-step', confirm ? '3' : '1');
    const focusTarget = confirm ? this.confirmStep?.querySelector('h3') : this.form.querySelector('input');
    focusTarget?.focus({ preventScroll: true });
    const header = document.querySelector('.section-header');
    const offset = (header ? header.getBoundingClientRect().height : 0) + 12;
    window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
  }

  makeOrderRef() {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${String(now.getFullYear()).slice(2)}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const random = Math.floor(1000 + Math.random() * 9000);
    return `YP-${date}-${random}`;
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR' }).format(cents / 100);
    } catch (error) {
      return `Rs. ${(cents / 100).toFixed(2)}`;
    }
  }

  readDetails() {
    const value = (selector) => this.querySelector(selector)?.value.trim() || '';
    return {
      name: value('[data-whatsapp-cart-name]'),
      phone: value('[data-whatsapp-cart-phone]'),
      email: value('[data-whatsapp-cart-email]'),
      city: value('[data-whatsapp-cart-city]'),
      address: value('[data-whatsapp-cart-address]'),
      message: value('[data-whatsapp-cart-message]'),
    };
  }

  buildOrder(details, cart) {
    const orderRef = this.makeOrderRef();
    const currency = cart.currency || 'INR';

    const lines = cart.items.map((item, index) => {
      const variant =
        item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
      const price =
        !this.hidePrices && item.final_line_price > 0 ? ` = ${this.formatMoney(item.final_line_price, currency)}` : '';
      return `${index + 1}. ${item.product_title}${variant} x ${item.quantity}${price}`;
    });

    const text = [
      `Hello ${this.shopName}, I would like to place this order.`,
      '',
      `*Order reference:* ${orderRef}`,
      '',
      '*Items*',
      ...lines,
      !this.hidePrices && cart.total_price > 0 ? `\n*Estimated total:* ${this.formatMoney(cart.total_price, currency)}` : null,
      '',
      '*My details*',
      `Name: ${details.name}`,
      `Phone: ${details.phone}`,
      details.email ? `Email: ${details.email}` : null,
      details.city ? `City: ${details.city}` : null,
      details.address ? `Address: ${details.address}` : null,
      details.message ? `\n*Notes:* ${details.message}` : null,
      '',
      'Please confirm my order.',
    ]
      .filter((line) => line !== null)
      .join('\n');

    return {
      ref: orderRef,
      details,
      cart,
      currency,
      text,
      waUrl: `https://wa.me/${this.phone}?text=${encodeURIComponent(text)}`,
    };
  }

  onSubmit(event) {
    event.preventDefault();
    this.clearErrors();

    const details = this.readDetails();
    if (!details.name) {
      this.showError('Please enter your name.');
      return;
    }
    if (details.phone.replace(/\D/g, '').length < 10) {
      this.showError('Please enter a valid phone number.');
      return;
    }
    if (!this.phone) {
      this.showError('WhatsApp ordering is not configured. Please contact the store.');
      return;
    }

    if (!this.cart) {
      this.submitLabel.textContent = 'Preparing your order…';
      this.refreshCart().then((cart) => {
        this.submitLabel.textContent = this.defaultLabel;
        if (cart) this.prepareAndOpen(details, cart, false);
        else this.showError('Could not load your cart. Please refresh the page and try again.');
      });
      return;
    }

    this.prepareAndOpen(details, this.cart, true);
  }

  prepareAndOpen(details, cart, openNow) {
    if (!cart.items || cart.items.length === 0) {
      this.showError('Your cart is empty.');
      return;
    }

    this.order = this.buildOrder(details, cart);
    if (this.openLink) this.openLink.href = this.order.waUrl;
    const ref = this.querySelector('[data-whatsapp-confirm-ref]');
    if (ref) ref.textContent = this.order.ref;

    if (openNow) window.open(this.order.waUrl, '_blank', 'noopener');
    this.showStep('confirm');
    if (!openNow && this.openLink) this.openLink.textContent = 'Open WhatsApp to send your order';
  }

  async sendWebhook(payload) {
    if (!this.webhookUrl) return;
    const body = JSON.stringify(payload);
    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      if (response.ok) return;
    } catch (error) {
      console.warn('WhatsApp order webhook CORS/network issue, retrying no-cors.', error);
    }
    try {
      await fetch(this.webhookUrl, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain' }, body });
    } catch (error) {
      console.warn('WhatsApp order webhook failed', error);
    }
  }

  async onConfirm() {
    if (!this.order) {
      this.showStep('details');
      return;
    }

    const { ref, details, cart, currency, text } = this.order;
    this.confirmButton.disabled = true;
    if (this.confirmLabel) this.confirmLabel.textContent = 'Completing your order…';

    try {
      await this.sendWebhook({
        source: 'shopify-whatsapp-cart-order',
        type: 'cart_order',
        status: 'sent_by_customer',
        notify_admin: true,
        order_ref: ref,
        shop: this.shopName,
        admin_phone: this.phone,
        buyer_name: details.name,
        buyer_phone: details.phone,
        buyer_email: details.email,
        buyer_city: details.city,
        buyer_address: details.address,
        buyer_message: details.message,
        inquiry_text: text,
        line_items: cart.items.map((item) => ({
          product_title: item.product_title,
          variant_title: item.variant_title,
          quantity: item.quantity,
          variant_id: item.variant_id,
          product_id: item.product_id,
          sku: item.sku || '',
          url: item.url ? `${window.location.origin}${item.url}` : '',
        })),
        item_count: cart.item_count,
        currency,
        cart_token: cart.token || '',
      });

      try {
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (clearError) {
        console.warn('Could not clear cart after WhatsApp order', clearError);
      }

      this.form.reset();
      this.showSuccess();
    } catch (error) {
      console.error('WhatsApp order confirmation failed', error);
      this.showError('Something went wrong. Please try again.', this.confirmError);
      this.confirmButton.disabled = false;
      if (this.confirmLabel) this.confirmLabel.textContent = "Yes, I've sent my order";
    }
  }

  showSuccess() {
    const { ref, details, cart, currency } = this.order;
    const panel = document.querySelector('[data-whatsapp-order-success]');
    if (!panel) {
      window.location.href = '/cart';
      return;
    }

    const items = cart.items
      .map((item) => {
        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<span class="yp-wa-success__variant">${this.escape(item.variant_title)}</span>`
            : '';
        let image = '';
        if (item.image) {
          const src = new URL(item.image, window.location.origin);
          src.searchParams.set('width', '120');
          image = `<img src="${this.escape(src.toString())}" alt="" width="60" height="60" loading="lazy">`;
        }
        const price = this.hidePrices
          ? ''
          : `<span class="yp-wa-success__price">${
              item.final_line_price > 0 ? this.formatMoney(item.final_line_price, currency) : 'Price on request'
            }</span>`;
        return `<li class="yp-wa-success__item">
            ${image}
            <span class="yp-wa-success__item-info">
              <strong>${this.escape(item.product_title)}</strong>
              ${variant}
              <span class="yp-wa-success__qty">Qty ${item.quantity}</span>
            </span>
            ${price}
          </li>`;
      })
      .join('');

    panel.querySelector('[data-success-name]').textContent = details.name.split(' ')[0];
    panel.querySelector('[data-success-phone]').textContent = details.phone;
    panel.querySelector('[data-success-ref]').textContent = ref;
    panel.querySelector('[data-success-items]').innerHTML = items;
    const total = panel.querySelector('[data-success-total]');
    if (total) total.textContent = cart.total_price > 0 ? this.formatMoney(cart.total_price, currency) : 'To be confirmed';

    const chatLink = panel.querySelector('[data-success-chat]');
    if (chatLink) {
      const chatText = `Hello ${this.shopName}, I have a question about my order ${ref}.`;
      chatLink.href = `https://wa.me/${this.phone}?text=${encodeURIComponent(chatText)}`;
    }

    document.querySelectorAll('[data-whatsapp-order-hide]').forEach((el) => {
      el.hidden = true;
    });
    const cartItems = document.querySelector('cart-items');
    if (cartItems) cartItems.closest('.shopify-section')?.setAttribute('hidden', '');
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => bubble.remove());

    document.body.classList.add('yp-order-complete');
    panel.hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    panel.querySelector('h2')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('whatsapp-cart-inquiry')) {
  customElements.define('whatsapp-cart-inquiry', WhatsAppCartInquiry);
}
